import Button from "@/components/atoms/Button";
import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/atoms/Form";
import Input from "@/components/atoms/Input";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState, useEffect } from "react";
import * as z from "zod";
import PasswordInput from "@/components/atoms/PasswordInput/PasswordInput";

const loginFormSchema = z.object({
  username: z.string().nonempty("Username is required"),
  password: z.string().nonempty("Password is required"),
});

const emailFormSchema = z.object({
  email: z.string().email("Invalid email address").nonempty("Email is required"),
});

type LoginFormData = z.infer<typeof loginFormSchema>;
type EmailFormData = z.infer<typeof emailFormSchema>;

type Props = {
  onSubmit: (data: LoginFormData) => void;
  onResetPassword: (data: EmailFormData) => void;
  onResendConfirmation: (data: EmailFormData) => void;
  onModeChange: (mode: "login" | "forgotPassword" | "resendConfirmation") => void;
  isSubmitting?: boolean;
  selectedMode: "login" | "forgotPassword" | "resendConfirmation";
};

export const useLoginForm = () =>
  useForm<LoginFormData>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: {
      username: "",
      password: "",
    },
  });

export const useEmailForm = () =>
  useForm<EmailFormData>({
    resolver: zodResolver(emailFormSchema),
    defaultValues: {
      email: "",
    },
  });

const LoginForm = ({
  onSubmit,
  onResetPassword,
  onResendConfirmation,
  onModeChange,
  isSubmitting,
  selectedMode,
}: Props) => {
  const [mode, setMode] = useState<"login" | "forgotPassword" | "resendConfirmation">(selectedMode);
  const [showPassword, setShowPassword] = useState(false);
  const loginForm = useLoginForm();
  const emailForm = useEmailForm();

  useEffect(() => {
    onModeChange(mode);
  }, [mode]);

  useEffect(() => {
    setMode(selectedMode);
  }, [selectedMode]);

  const handleLoginSubmit = (data: LoginFormData) => {
    onSubmit(data);
  };

  const handleEmailSubmit = (callback: (data: EmailFormData) => void) => {
    return (data: EmailFormData) => {
      callback(data);
    };
  };

  return (
    <div>
      {mode === "login" && (
        <Form {...loginForm}>
          <form
            onSubmit={loginForm.handleSubmit(handleLoginSubmit)}
            className="space-y-8 w-full"
          >
            {/* Username Field */}
            <FormField
              control={loginForm.control}
              name="username"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>Username</FormLabel>
                  <FormControl>
                    <Input {...field} type="text" placeholder="Username" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Password Field */}
            <FormField
              control={loginForm.control}
              name="password"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <PasswordInput field={field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Login Button */}
            <Button type="submit" className="flex w-full p-4 rounded-xl">
              
              {isSubmitting ? "Submitting..." : "LOGIN"}
            </Button>

            {/* Links */}
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => setMode("forgotPassword")}
                className="text-md font-bold text-gray-500 hover:underline"
              >
                Forgot your password?
              </button>
              <button
                type="button"
                onClick={() => setMode("resendConfirmation")}
                className="text-md font-bold text-gray-500 hover:underline"
              >
                Didn't receive confirmation instructions?
              </button>
            </div>
          </form>
        </Form>
      )}

      {(mode === "forgotPassword" || mode === "resendConfirmation") && (
        <Form {...emailForm}>
          <form
            onSubmit={emailForm.handleSubmit(
              handleEmailSubmit(
                mode === "forgotPassword" ? onResetPassword : onResendConfirmation
              )
            )}
            className="space-y-8 w-full"
          >
            {/* Email Field */}
            <FormField
              control={emailForm.control}
              name="email"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input {...field} type="email" placeholder="Email" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Submit Button */}
            <Button type="submit" className="flex w-full p-4 rounded-xl">
            {isSubmitting
              ? "Submitting"
              : mode === "forgotPassword"
              ? "Send Reset Password Instructions"
              : "Resend Confirmation Instructions"}
            </Button>
          </form>
        </Form>
      )}
    </div>
  );
};

export default LoginForm;
